import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, Shield, CreditCard, Info } from 'lucide-react';
import { ActiveCustomer } from '../../../types/organizations';
import { getCategoryStyling } from '../../../utils/categoryStyling';
import { getLatestAnalysis } from '../../../utils/analysis';

interface CustomerCardProps {
  customer: ActiveCustomer;
  organizationId: string;
  reAnalyzingCustomers: Set<string>;
  onReAnalyze: (xeroContactId: string) => void;
  onOpenPointOfContactModal: (customer: ActiveCustomer) => void;
  onOpenNotesModal: (customer: ActiveCustomer) => void;
  onOpenInvoicesModal: (customer: ActiveCustomer) => void;
  onOpenReasoningModal: (customer: ActiveCustomer) => void;
  onOpenReliabilityModal: (customer: ActiveCustomer) => void;
  onOpenAPArchetypesModal: (customer: ActiveCustomer) => void;
  onOpenGovernanceOverlaysModal: (customer: ActiveCustomer) => void;
  onConfirmDisconnectAPArchetype: (archetypeId: string, archetypeName: string, customerId: string) => void;
  onConfirmDisconnectGovernanceOverlay: (overlayId: string, overlayName: string, customerId: string) => void;
  onOpenCustomerCreditModal?: (customer: ActiveCustomer) => void;
  hasIndividualizedCredit?: (customerId: string) => boolean;
  onOpenCustomerCreditView?: (customer: ActiveCustomer, initialValues: { credit_terms?: string | null; credit_terms_desc?: string | null; credit_limit?: number | null; credit_limit_desc?: string | null; hasIndividualized?: boolean }) => void;
}

// Helper function to get customer insight category
const getCustomerInsightCategory = (customer: ActiveCustomer): string | null => {
  const insight = getLatestAnalysis(customer.agent_analyses, 'customer_insight');
  return insight?.output_json?.selected_category || null;
};

// Helper function to get customer profile summary
const getCustomerProfileSummary = (customer: ActiveCustomer): string | null => {
  const analysis = getLatestAnalysis(customer.agent_analyses, 'customer_profile_summary');
  return analysis?.output_json?.summary || null;
};

// Helper function to get payment reliability score
const getPaymentReliabilityScore = (customer: ActiveCustomer): { score: number; confidence: string } | null => {
  const analysis = getLatestAnalysis(customer.agent_analyses, 'payment_reliability_score');
  if (!analysis?.output_json) return null;

  const score = typeof analysis.output_json.score === 'string'
    ? parseInt(analysis.output_json.score) || 0
    : analysis.output_json.score || 0;

  const confidence = analysis.output_json.confidence || 'Unknown';

  return { score, confidence };
};

// Helper function to get customer latest analyzed timestamp
const getCustomerLatestAnalyzedAt = (customer: ActiveCustomer): Date | null => {
  let latest: Date | null = null;
  customer.agent_analyses.forEach(analysis => {
    if (analysis.analyzed_at) {
      const analysisDate = new Date(analysis.analyzed_at);
      if (!latest || analysisDate > latest) {
        latest = analysisDate;
      }
    }
  });
  return latest;
};

const CustomerCard: React.FC<CustomerCardProps> = ({
  customer,
  organizationId,
  reAnalyzingCustomers,
  onReAnalyze,
  onOpenPointOfContactModal,
  onOpenNotesModal,
  onOpenInvoicesModal,
  onOpenReasoningModal,
  onOpenReliabilityModal,
  onOpenAPArchetypesModal,
  onOpenGovernanceOverlaysModal,
  onConfirmDisconnectAPArchetype,
  onConfirmDisconnectGovernanceOverlay,
  onOpenCustomerCreditModal,
  hasIndividualizedCredit,
  onOpenCustomerCreditView,
}) => {
  const insightCategory = getCustomerInsightCategory(customer);
  const categoryStyling = insightCategory ? getCategoryStyling(insightCategory) : null;
  const profileSummary = getCustomerProfileSummary(customer);
  const paymentScore = getPaymentReliabilityScore(customer);
  const latestAnalyzedAt = getCustomerLatestAnalyzedAt(customer);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
      {/* Customer Header */}
      <div>
        <div className="flex justify-between items-start mb-2">
          <div className="flex-1 min-w-0">
            <Link
              to={`/organizations/${organizationId}/customers/${customer.id}`}
              className="text-lg font-semibold text-blue-600 hover:text-blue-800 hover:underline cursor-pointer truncate transition-colors block"
            >
              {customer.name}
            </Link>
          </div>
          {/* Customer Insight Category - moved to right edge */}
          {insightCategory && categoryStyling && (
            <button
              onClick={() => onOpenReasoningModal(customer)}
              className={`px-3 py-1 ${categoryStyling.bg} ${categoryStyling.text} text-xs font-medium rounded-full flex-shrink-0 hover:opacity-80 transition-opacity cursor-pointer ml-3`}
            >
              {insightCategory}
            </button>
          )}
        </div>
        
        {/* Connected AP Archetypes and Governance Overlays */}
        {(customer.ap_archetypes && customer.ap_archetypes.length > 0) || (customer.governance_overlays && customer.governance_overlays.length > 0) ? (
          <div className="mb-3">
            <div className="flex flex-wrap gap-1">
              {/* AP Archetypes */}
              {customer.ap_archetypes && customer.ap_archetypes.map((archetype) => (
                <span
                  key={archetype.connection_id}
                  className="group inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 hover:bg-purple-200 transition-colors"
                  title={archetype.description}
                >
                  <Building2 className="w-3 h-3 mr-1" />
                  {archetype.name}
                  <button
                    onClick={() => onConfirmDisconnectAPArchetype(archetype.id, archetype.name, customer.id)}
                    className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity text-purple-600 hover:text-purple-800"
                    title="Remove AP archetype"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              ))}
              {/* Governance Overlays */}
              {customer.governance_overlays && customer.governance_overlays.map((overlay) => (
                <span
                  key={overlay.connection_id}
                  className="group inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 hover:bg-orange-200 transition-colors"
                  title={overlay.description}
                >
                  <Shield className="w-3 h-3 mr-1" />
                  {overlay.name}
                  <button
                    onClick={() => onConfirmDisconnectGovernanceOverlay(overlay.id, overlay.name, customer.id)}
                    className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity text-orange-600 hover:text-orange-800"
                    title="Remove governance overlay"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              ))}
            </div>
          </div>
        ) : null}
        
        {/* Actions Section - moved below customer name */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center space-x-2">
            {/* Re-analyze Button */}
            <button
              onClick={() => onReAnalyze(customer.xero_contact_id)}
              disabled={reAnalyzingCustomers.has(customer.xero_contact_id)}
              className={`p-2 rounded-lg transition-colors ${reAnalyzingCustomers.has(customer.xero_contact_id)
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                }`}
              title="Re-analyze this customer"
            >
              {reAnalyzingCustomers.has(customer.xero_contact_id) ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              )}
            </button>
            {/* Message Circle Button */}
            <button
              onClick={() => onOpenPointOfContactModal(customer)}
              className="p-2 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 transition-colors"
              title="Start conversation"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </button>
            {/* Notes Button */}
            <button
              onClick={() => onOpenNotesModal(customer)}
              className="p-2 rounded-lg bg-yellow-50 text-yellow-600 hover:bg-yellow-100 transition-colors"
              title="View and manage notes"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            {/* AP Archetypes Button */}
            <button
              onClick={() => onOpenAPArchetypesModal(customer)}
              className="p-2 rounded-lg bg-gray-50 text-gray-400 hover:bg-gray-100 transition-colors"
              title={customer.ap_archetypes && customer.ap_archetypes.length > 0 ? `${customer.ap_archetypes.length} AP archetype${customer.ap_archetypes.length !== 1 ? 's' : ''} connected` : 'Assign AP archetypes'}
            >
              <Building2 className="w-4 h-4" />
            </button>
            {/* Governance Overlays Button */}
            <button
              onClick={() => onOpenGovernanceOverlaysModal(customer)}
              className="p-2 rounded-lg bg-gray-50 text-gray-400 hover:bg-gray-100 transition-colors"
              title={customer.governance_overlays && customer.governance_overlays.length > 0 ? `${customer.governance_overlays.length} governance overlay${customer.governance_overlays.length !== 1 ? 's' : ''} connected` : 'Assign governance overlays'}
            >
              <Shield className="w-4 h-4" />
            </button>
            {/* Customer Credit Terms/Limit Button */}
            {onOpenCustomerCreditModal && (
              <button
                onClick={() => onOpenCustomerCreditModal(customer)}
                className={`p-2 rounded-lg transition-colors ${hasIndividualizedCredit && hasIndividualizedCredit(customer.id) ? 'bg-green-50 text-green-600 hover:bg-green-100' : 'bg-gray-50 text-gray-400 hover:bg-gray-100'}`}
                title="Set credit terms / limit"
              >
                <CreditCard className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        {/* Individualized Credit Terms / Limit (if active) */}
        {(() => {
          const rec: any = (customer as any).individualized_credit;
          const isActive = rec && rec.status === 'active';
          if (!isActive) return null;
          const terms: string = rec?.credit_terms || '';
          const limit: number | null = rec?.credit_limit != null ? rec.credit_limit : null;
          const limitStr = limit != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(limit) : '';
          const values = {
            credit_terms: terms,
            credit_terms_desc: rec?.credit_terms_desc || '',
            credit_limit: limit,
            credit_limit_desc: rec?.credit_limit_desc || '',
            hasIndividualized: true
          };
          return (
            <div className="mt-2 text-sm text-gray-700 flex items-center space-x-2">
              {(terms || limitStr) && (
                <span className="inline-block px-2 py-1 rounded bg-blue-50 text-blue-700">
                  {terms || 'N/A'} {(terms || '').length > 0 || limitStr ? '/' : ''} {limitStr || 'N/A'}
                </span>
              )}
              {onOpenCustomerCreditView && (
                <button
                  className="text-gray-400 hover:text-gray-600"
                  title="View details"
                  onClick={() => onOpenCustomerCreditView(customer, values)}
                >
                  <Info className="w-4 h-4" />
                </button>
              )}
            </div>
          );
        })()}

        {/* Last Analyzed Timestamp - moved below actions */}
        {latestAnalyzedAt && (
          <div className="text-[11px] text-gray-500 mt-2">
            Last analyzed: {latestAnalyzedAt.toLocaleDateString()} at {latestAnalyzedAt.toLocaleTimeString()}
          </div>
        )}
      </div>
      
      {/* Latest Note Preview - Full Width */}
      {customer.latest_note && (
        <div className="mb-4 flex items-center w-full">
          <div className="w-1 h-4 bg-gray-400 mr-3 flex-shrink-0"></div>
          <span className="text-sm text-gray-600 italic line-clamp-2 flex-1 min-w-0">
            {customer.latest_note.content}
          </span>
        </div>
      )}

      {/* Customer Profile Summary */}
      {profileSummary && (
        <div className="mb-4">
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-xs text-gray-600 leading-relaxed">
              {profileSummary}
            </p>
          </div>
        </div>
      )}

      {/* Contact Information */}
      <div className="space-y-3 mb-4">
        {/* Email */}
        {customer.email_address && (
          <div className="flex items-center">
            <svg className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span className="text-sm text-gray-700 truncate">{customer.email_address}</span>
          </div>
        )}

        {/* Phone Numbers - Only show if there are phones with actual data */}
        {customer.phones && customer.phones.length > 0 && customer.phones.some(phone => phone.number && phone.number.trim()) && (
          <div className="flex items-center">
            <svg className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            <div className="flex-1">
              {customer.phones.filter(phone => phone.number && phone.number.trim()).map((phone, index) => (
                <div key={index} className="text-sm text-gray-700">
                  <span className="font-medium">{phone.type}:</span> {phone.number}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Invoice Metadata */}
      <div className="border-t border-gray-200 pt-3">
        <div className="grid grid-cols-3 gap-2 text-xs mb-3">
          <button
            onClick={() => onOpenInvoicesModal(customer)}
            className="text-center p-2 bg-blue-50 rounded hover:bg-blue-100 transition-colors cursor-pointer w-full"
          >
            <div className="font-semibold text-blue-900 text-sm">{customer.invoice_metadata.outstanding_invoice_count}/{customer.invoice_metadata.last_12m_invoice_count}</div>
            <div className="text-blue-600">LTM Invoices</div>
          </button>
          <button
            onClick={() => onOpenInvoicesModal(customer)}
            className="text-center p-2 bg-green-50 rounded hover:bg-green-100 transition-colors cursor-pointer w-full"
          >
            <div className="font-semibold text-green-900 text-sm">
              ${customer.invoice_metadata.total_receivable.toLocaleString()}
            </div>
            <div className="text-green-600">Receivable</div>
          </button>
          <button
            onClick={() => onOpenInvoicesModal(customer)}
            className="text-center p-2 bg-purple-50 rounded hover:bg-purple-100 transition-colors cursor-pointer w-full"
          >
            <div className="font-semibold text-purple-900 text-sm">
              ${customer.invoice_metadata.last_12m_value.toLocaleString()}
            </div>
            <div className="text-purple-600">LTM Value</div>
          </button>
        </div>

        {/* Reliability Score and Overdue */}
        {paymentScore && (
          <div className="mt-4">
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div
                className="cursor-pointer"
                onClick={() => onOpenReliabilityModal(customer)}
              >
                <div className="text-center p-2 bg-orange-50 rounded hover:bg-orange-100 transition-colors">
                  <div className="font-semibold text-orange-900 text-sm">{paymentScore.score}/10</div>
                  <div className="text-orange-600 text-[10px]">Reliability</div>
                </div>
                <div className="text-left text-gray-500 text-[9px] ml-1">Confidence: {paymentScore.confidence}</div>
              </div>
              {(customer.invoice_metadata.overdue_invoice_count ?? 0) > 0 && (
                <div className="text-center p-2 bg-red-50 rounded">
                  <div className="font-semibold text-red-900 text-sm">{customer.invoice_metadata.overdue_invoice_count}</div>
                  <div className="text-red-600 text-[10px]">Overdue</div>
                </div>
              )}
              <div></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerCard;
